import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { useFuelCardsRepository } from "../../../api/repositories/FuelCardsRepository";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { DatePicker } from "@mui/x-date-pickers";
import {AMOUNT_REG_EXP, DECIMAL_6_DIGITS, DECIMAL_9_DIGITS} from "../constants/FuelsConstants";
import { useFuelStatuses } from "../constants/useFuelStatuses";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { useFuelCategories } from "../constants/useFuelCategories";
import {formatDate, formatToFloat} from "../../../utils/FormatUtils";

interface FuelCardModalProps {
    open: boolean,
    setOpen: Function,
    fuelCardProp: any,
    isReadonly: boolean
}

const FuelCardDetail = ({open, setOpen, fuelCardProp, isReadonly}: FuelCardModalProps) => {
    const {t} = useTranslation('finance', {keyPrefix: 'fuel'});
    const {id} = useParams();
    const {updateFuel} = useFuelCardsRepository();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const [fuelCard, setFuelCard] = useState<any>(undefined);
    const {getStatus} = useFuelStatuses();
    const {getCategories} = useFuelCategories();

    useEffect(() => {
        if (open && fuelCardProp) {
            setFuelCard(fuelCardProp);
        }
    }, [open]);

    useEffect(() => {
        if (fuelCard) {
            reset(retrieveInitialValues());
        }
    }, [fuelCard])

    const retrieveInitialValues = () => {
        if (fuelCard) {
            return {
                reportId: fuelCard.reportId,
                id: fuelCard.id,
                cardCustomer: fuelCard.cardCustomer ? fuelCard.cardCustomer : "",
                raNumber: fuelCard.raNumber ? fuelCard.raNumber : "",
                vehicleSapNumber: fuelCard.vehicleSapNumber ? fuelCard.vehicleSapNumber : "",
                cardNumber: fuelCard.cardNumber ? fuelCard.cardNumber : "",
                invoiceNumber: fuelCard.invoiceNumber ? fuelCard.invoiceNumber : "",
                invoiceDate: fuelCard.invoiceDate ? fuelCard.invoiceDate : null,
                status: fuelCard.status ? fuelCard.status : "",
                category: fuelCard.category ? fuelCard.category : "",
                transactionDatetime: fuelCard.transactionDatetime ? fuelCard.transactionDatetime : null,
                productType: fuelCard.productType ? fuelCard.productType : "",
                quantity: fuelCard.quantity ? fuelCard.quantity : "",
                currency: fuelCard.currency ? fuelCard.currency : "",
                unitPrice: fuelCard.unitPrice ? formatToFloat(fuelCard.unitPrice, 0.01) : "",
                amount: fuelCard.amount ? formatToFloat(fuelCard.amount, 0.01) : "",
                supplier: fuelCard.supplier ? fuelCard.supplier : "",
                storeName: fuelCard.storeName ? fuelCard.storeName : "",
                storeAddress: fuelCard.storeAddress ? fuelCard.storeAddress : "",
                reportFileName: fuelCard.reportFileName ? fuelCard.reportFileName : "",
                reportIdCreatedDate: fuelCard.reportIdCreatedDate ? fuelCard.reportIdCreatedDate : null,
                note: fuelCard.note ? fuelCard.note : ""
            }
        } else {
            return {
                reportId: "",
                id: "",
                cardCustomer: "",
                raNumber: "",
                vehicleSapNumber: "",
                cardNumber: "",
                invoiceNumber: "",
                invoiceDate: null,
                status: "",
                category: "",
                transactionDatetime: null,
                productType: "",
                quantity: "",
                currency: "",
                unitPrice: "",
                amount: "",
                supplier: "",
                storeName: "",
                storeAddress: "",
                reportFileName: "",
                reportIdCreatedDate: null,
                note: ""
            }
        }
    }

    const validationSchema = Yup.object({
        cardCustomer: Yup.string(),
        raNumber: Yup.string().nullable().matches(DECIMAL_6_DIGITS, {
            excludeEmptyString: true,
            message: t('messages.raLength')
        }).test("raNumberTest", t('messages.raOrVehicleNumberMandatory'),
            (value, context) => {
                return (context.parent.vehicleSapNumber || (value && !context.parent.vehicleSapNumber))
            }).typeError(t(' messages.raOrVehicleNumberMandatory')),
        vehicleSapNumber: Yup.string().nullable().matches(DECIMAL_9_DIGITS, {
            excludeEmptyString: true,
            message: t('messages.vehicleCodeLength')
        }).test("vehicleSapNumberTest", t('messages.raOrVehicleNumberMandatory'),
            (value, context) => {
                return (context.parent.raNumber || (value && !context.parent.raNumber))
            }).typeError(t(' messages.raOrVehicleNumberMandatory')),
        cardNumber: Yup.string(),
        invoiceNumber: Yup.string(),
        invoiceDate: Yup.date().nullable().typeError(t('messages.invalidDate')),
        status: Yup.string(),
        category: Yup.string(),
        transactionDatetime: Yup.date().nullable().typeError(t('messages.invalidDate')),
        productType: Yup.string().required(),
        quantity: Yup.string().required(),
        currency: Yup.string(),
        unitPrice: Yup.string().required().matches(AMOUNT_REG_EXP, {
            excludeEmptyString: true,
            message: t('messages.invalidUnitPrice')
        }),
        amount: Yup.string().required().matches(AMOUNT_REG_EXP, {
            excludeEmptyString: true,
            message: t('messages.invalidAmount')
        }),
        supplier: Yup.string(),
        storeName: Yup.string().required(),
        storeAddress: Yup.string(),
        reportFileName: Yup.string(),
        reportIdCreatedDate: Yup.date().nullable().typeError(t('messages.invalidDate')),
        note: Yup.string()
    });

    const {handleSubmit, control, reset, watch} = useForm({
        defaultValues: retrieveInitialValues(),
        resolver: yupResolver(validationSchema)
    });

    const onSubmit = (payload: any) => {
        let data = {...payload};
        data.amount *= 100;
        data.unitPrice *= 100;
        updateFuel({fuelCard: data}).then((data) => {
            if (data && data.status === "OK") {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t("messages.updateSuccess"),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
                setOpen(false);
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t(`messages.update.${data.message}`),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        })
    }

    return (
        <Dialog open={open} maxWidth={false}>
            <form onSubmit={handleSubmit(data => onSubmit(data), errors => console.log(errors))} noValidate>

                <DialogTitle>
                    <Grid container item xs={12}>
                        <Grid item xs={10}>
                            <Typography variant={'body1'} fontWeight={'bold'}>
                                {t(isReadonly ? 'details.title' : 'details.titleEdit', {id: fuelCard?.id})}
                            </Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <IconButton sx={{position: 'absolute', right: 16, top: 12,}} onClick={() => {
                                setOpen(false)
                            }}>
                                <CloseRounded/>
                            </IconButton>
                        </Grid>
                    </Grid>

                </DialogTitle>
                <DialogContent>
                    <Grid container direction={'column'} rowGap={2}>
                        <Grid container item>
                            <Grid item xs>
                                <Box style={{display: 'flex', alignItems: 'center', gap: '1ch'}}>
                                    <Typography variant={'body2'} fontWeight={'bold'}>
                                        {t('details.generalInfo').toUpperCase()}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid container item columnSpacing={3} rowSpacing={1}>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"cardCustomer"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={true}
                                                value={field.value}
                                                label={t("details.cardCustomer")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"raNumber"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={isReadonly}
                                                id="raNumber"
                                                value={field.value}
                                                label={t("details.raNumber")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                                inputProps={{ maxLength: 6 }}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"vehicleSapNumber"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={isReadonly}
                                                id="vehicleSapNumber"
                                                value={field.value}
                                                label={t("details.carNumber")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                                inputProps={{ maxLength: 9 }}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"cardNumber"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={true}
                                                id="cardNumber"
                                                value={field.value}
                                                label={t("details.cardNumber")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"invoiceNumber"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={true}
                                                id="invoiceNumber"
                                                value={field.value}
                                                label={t("details.invoiceNumber")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"invoiceDate"}
                                        render={({field, fieldState}) => (
                                            <DatePicker
                                                label={t("details.invoiceDate")}
                                                value={field.value}
                                                onChange={field.onChange}
                                                disabled={true}
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        disabled={true}
                                                        type="text"
                                                        label={t("details.invoiceDate")}
                                                        onChange={field.onChange}
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}
                                                        InputLabelProps={{shrink: true}}
                                                    />}
                                                inputFormat={"L"}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"status"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={true}
                                                label={t("details.status")}
                                                value={getStatus(field.value)}
                                                InputLabelProps={{shrink: true}}
                                            >
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                {fuelCard?.errorMessage &&
                                    <Grid item xs={3}>
                                    <Tooltip arrow title={t('messages.' + fuelCard.errorMessage)}>
                                        <InfoRoundedIcon color="primary"/>
                                    </Tooltip>
                                </Grid>}
                            </Grid>
                        </Grid>
                        <Grid container item>
                            <Grid item xs>
                                <Box style={{display: 'flex', alignItems: 'center', gap: '1ch'}}>
                                    <Typography variant={'body2'} fontWeight={'bold'}>
                                        {t('details.consumptionDetails').toUpperCase()}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid container item columnSpacing={3} rowSpacing={1}>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"category"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={true}
                                                label={t("details.category")}
                                                value={getCategories(field.value)}
                                                InputLabelProps={{shrink: true}}
                                            >
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        fullWidth
                                        disabled={true}
                                        type="text"
                                        label={t("details.refuellingDate")}
                                        value={formatDate(watch("transactionDatetime"))}
                                        InputLabelProps={{shrink: true}}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"productType"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                required
                                                disabled={true}
                                                id="productType"
                                                value={field.value}
                                                label={t("details.productType")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"quantity"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                required
                                                disabled={true}
                                                id="quantity"
                                                value={field.value}
                                                label={t("details.quantity")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"currency"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={true}
                                                id="currency"
                                                value={field.value}
                                                label={t("details.currency")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}

                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"unitPrice"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label={t("details.unitPrice")}
                                                value={field.value}
                                                disabled={true}
                                                id="unitPrice"
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"amount"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                required
                                                disabled={true}
                                                id="amount"
                                                value={field.value}
                                                label={t("details.amount")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}

                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container item>
                            <Grid item xs>
                                <Box style={{display: 'flex', alignItems: 'center', gap: '1ch'}}>
                                    <Typography variant={'body2'} fontWeight={'bold'}>
                                        {t('details.supplierDetails').toUpperCase()}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid container item columnSpacing={3} rowSpacing={1}>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"supplier"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={true}
                                                id="supplier"
                                                value={field.value}
                                                label={t("details.supplier")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}

                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"storeName"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                required
                                                disabled={true}
                                                id="storeName"
                                                value={field.value}
                                                label={t("details.store")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"storeAddress"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={true}
                                                id="storeAddress"
                                                value={field.value}
                                                label={t("details.storeAddress")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"reportFileName"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                disabled={true}
                                                id="reportFileName"
                                                value={field.value}
                                                label={t("details.fileName")}
                                                type="text"
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                InputLabelProps={{shrink: true}}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        fullWidth
                                        disabled={true}
                                        type="text"
                                        label={t("details.importDate")}
                                        value={formatDate(watch("reportIdCreatedDate"))}
                                        InputLabelProps={{shrink: true}}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    control={control}
                                    name={"note"}
                                    render={({field, fieldState}) => (
                                        <TextField
                                            fullWidth
                                            disabled={true}
                                            id="note"
                                            value={field.value}
                                            label={t("details.note")}
                                            type="text"
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            InputLabelProps={{shrink: true}}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                </DialogContent>
                <DialogActions className="ModalFooter">
                    <Grid container>
                        <Grid container item xs={12} rowGap={2} rowSpacing={1} columnSpacing={3}
                              justifyContent={"flex-end"}>
                            {!isReadonly ?
                                <>
                                    <Grid item>
                                        <Button variant={"outlined"} color={"accent"}
                                                onClick={() => {
                                                    setOpen(false);
                                                }}>
                                            {t('details.buttons.cancel')}
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button color={"accent"} type={"submit"}>
                                            {t('details.buttons.save')}
                                        </Button>
                                    </Grid>
                                </>
                                :
                                <Grid item>
                                    <Button color={"accent"}
                                            onClick={() => {
                                                setOpen(false);
                                                reset(retrieveInitialValues());
                                            }}>
                                        {t('details.buttons.close')}
                                    </Button>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default FuelCardDetail;